import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * ErrorBoundary (Iter42) — catches render errors from any child in the admin
 * tree so a single malformed row (e.g. a transaction with a null type) can't
 * blank the entire dashboard. Renders a friendly recovery card with the raw
 * error visible under a details toggle for debugging.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep this in the console for the developer; production still shows the
    // recovery UI below.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="p-6 md:p-10 max-w-2xl mx-auto" data-testid="error-boundary-fallback">
        <div className="bg-white border border-rose-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-lg font-bold">Something on this screen misbehaved</h2>
              <p className="text-sm text-kudi-ink/70 mt-1">
                The rest of the admin console is fine. Try again, or reload the page.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={this.reset}
                  className="text-sm font-semibold bg-kudi-green text-white px-3 py-2 rounded-lg hover:bg-kudi-green-dark transition inline-flex items-center gap-1"
                  data-testid="error-boundary-retry"
                >
                  <RefreshCw className="w-3 h-3" /> Try again
                </button>
                <button
                  onClick={this.reload}
                  className="text-sm font-semibold bg-white border border-border text-kudi-ink px-3 py-2 rounded-lg hover:bg-accent/40 transition"
                  data-testid="error-boundary-reload"
                >
                  Reload page
                </button>
              </div>
              {this.state.error && (
                <details className="mt-4 text-xs text-kudi-ink/50">
                  <summary className="cursor-pointer select-none">Show error detail</summary>
                  <pre className="mt-2 p-2 bg-kudi-cream rounded overflow-auto text-[11px] whitespace-pre-wrap break-words">
                    {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
